import React from "react";
import { Helmet } from "@dr.pogodin/react-helmet";

const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = "website",
  university = null,
  blog = null,
}) => {
  // Default values
  const defaultTitle = "Grad4Grad - Your Gateway to International Education";
  const defaultDescription =
    "Discover top universities worldwide, get personalized study abroad guidance, scholarship opportunities, and expert advice for international students. Start your global education journey today!";
  const defaultKeywords =
    "study abroad, international universities, scholarships, university admissions, study in USA, study in Canada, international students, graduate school, masters degree, PhD programs";
  const defaultImage = "https://grad4grad.com/logo512.png";
  const defaultUrl = "https://grad4grad.com";

  // Use provided values or defaults
  const pageTitle = title || defaultTitle;
  const pageDescription = description || defaultDescription;
  const pageKeywords = keywords || defaultKeywords;
  const pageImage = image || defaultImage;
  const pageUrl = url || defaultUrl;

  // Generate university-specific schema if university data is provided
  const generateUniversitySchema = () => {
    if (!university) return null;

    return {
      "@context": "https://schema.org",
      "@type": "EducationalOrganization",
      name: university["University Name"] || university.name,
      url: pageUrl,
      description: `Learn about ${
        university["University Name"] || university.name
      } - tuition fees, admission requirements, and programs for international students.`,
      address: {
        "@type": "PostalAddress",
        addressLocality:
          university.city || university["City, State"]?.split(",")[0]?.trim(),
        addressRegion:
          university.state || university["City, State"]?.split(",")[1]?.trim(),
        addressCountry: university.country || "Unknown",
      },
      telephone: university.phone || "",
      email: university.email || "",
      foundingDate: university.founded || "",
      numberOfStudents:
        university["Total Enrolled"] || university.totalEnrolled,
      alumni: {
        "@type": "Person",
        name: "International Alumni",
      },
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Academic Programs",
        itemListElement: [
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Course",
              name: "Graduate Programs",
              description:
                "Masters and PhD programs for international students",
            },
          },
        ],
      },
    };
  };

  // Generate blog-specific schema if blog data is provided
  const generateBlogSchema = () => {
    if (!blog) return null;

    return {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: blog.title || pageTitle,
      description: blog.description || pageDescription,
      image: blog.image || pageImage,
      author: {
        "@type": "Organization",
        name: "Grad4Grad",
      },
      publisher: {
        "@type": "Organization",
        name: "Grad4Grad",
        logo: {
          "@type": "ImageObject",
          url: "https://grad4grad.com/logo512.png",
        },
      },
      datePublished: blog.publishedDate || new Date().toISOString(),
      dateModified: blog.modifiedDate || new Date().toISOString(),
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": pageUrl,
      },
    };
  };

  // Generate FAQ schema for common questions
  const generateFAQSchema = () => {
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "How do I choose the right university for study abroad?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Consider factors like academic reputation, program offerings, location, cost, and available scholarships. Use our university comparison tools to make an informed decision.",
          },
        },
        {
          "@type": "Question",
          name: "What are the requirements for international student admission?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Requirements typically include academic transcripts, standardized test scores (GRE/GMAT/TOEFL/IELTS), letters of recommendation, statement of purpose, and proof of financial support.",
          },
        },
        {
          "@type": "Question",
          name: "How can I find scholarships for international students?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Research university-specific scholarships, government-funded programs, private foundations, and international organizations. Start your search early and apply to multiple opportunities.",
          },
        },
      ],
    };
  };

  // Generate organization schema
  const generateOrganizationSchema = () => {
    return {
      "@context": "https://schema.org",
      "@type": "EducationalOrganization",
      name: "Grad4Grad",
      url: "https://grad4grad.com",
      logo: "https://grad4grad.com/logo512.png",
      description: "Your Gateway to International Education",
      address: {
        "@type": "PostalAddress",
        addressCountry: "Global",
      },
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "customer service",
        email: "contact@grad4grad.com",
      },
      sameAs: ["https://grad4grad.com"],
    };
  };

  // Generate website schema
  const generateWebsiteSchema = () => {
    return {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Grad4Grad",
      url: "https://grad4grad.com",
      description: "Your Gateway to International Education",
      potentialAction: {
        "@type": "SearchAction",
        target:
          "https://grad4grad.com/universities?search={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    };
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="title" content={pageTitle} />
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <meta name="author" content="Grad4Grad" />
      <meta
        name="robots"
        content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />

      {/* Canonical URL */}
      <link rel="canonical" href={pageUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:image:width" content="512" />
      <meta property="og:image:height" content="512" />
      <meta property="og:image:alt" content={pageTitle} />
      <meta property="og:site_name" content="Grad4Grad" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:locale:alternate" content="en_CA" />
      <meta property="og:locale:alternate" content="en_GB" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={pageUrl} />
      <meta property="twitter:title" content={pageTitle} />
      <meta property="twitter:description" content={pageDescription} />
      <meta property="twitter:image" content={pageImage} />
      <meta property="twitter:image:alt" content={pageTitle} />
      <meta property="twitter:site" content="@grad4grad" />
      <meta property="twitter:creator" content="@grad4grad" />

      {/* Additional Meta Tags */}
      <meta name="theme-color" content="#667eea" />
      <meta name="msapplication-TileColor" content="#667eea" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="Grad4Grad" />
      <meta name="application-name" content="Grad4Grad" />
      <meta name="mobile-web-app-capable" content="yes" />

      {/* Schema.org structured data */}
      {university && (
        <script type="application/ld+json">
          {JSON.stringify(generateUniversitySchema())}
        </script>
      )}

      {blog && (
        <script type="application/ld+json">
          {JSON.stringify(generateBlogSchema())}
        </script>
      )}

      {/* Organization Schema */}
      <script type="application/ld+json">
        {JSON.stringify(generateOrganizationSchema())}
      </script>

      {/* Website Schema */}
      <script type="application/ld+json">
        {JSON.stringify(generateWebsiteSchema())}
      </script>

      {/* FAQ Schema for educational content */}
      {(type === "website" || type === "article") && (
        <script type="application/ld+json">
          {JSON.stringify(generateFAQSchema())}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
